import { Location } from "@angular/common";
import { Component, HostBinding, Input } from "@angular/core";
import { UsersApi } from "@tytapp/api";
import { MessageDialogComponent } from "@tytapp/app/message-dialog/message-dialog.component";
import { AppConfig, Logger, Shell, sleep } from "@tytapp/common";
import { environment } from "@tytapp/environment";
import { User, UserAuthToken } from "@tytapp/models";
import { LoginComponent, UserService } from "@tytapp/user";
import { SubSink } from "subsink";

@Component({
    selector: 'tyt-user-signup-block',
    templateUrl: './user-signup-block.component.html',
    styleUrls: ['./user-signup-block.component.scss']
})
export class UserSignupBlockComponent {
    constructor(
        private userService : UserService,
        private shell : Shell,
        private appConfig : AppConfig,
        private userApi : UsersApi,
        private location : Location
    ) {
    }

    user : User;
    subsink = new SubSink();

    private _knownFirstName : string;
    private _knownEmail : string;
    
    justSignedUp = false;

    @HostBinding('class.signed-in')
    get alreadySignedIn() {
        return this.user != null && this.phase !== 'success';
    }

    phase : string = 'form';
    errorMessage : string;
    emailErrorMessage : string;

    @Input()
    get knownFirstName() {
        return this._knownFirstName;
    }

    set knownFirstName(value) {
        this._knownFirstName = value;
        setTimeout(() => {
            if (!this.firstName)
                this.firstName = value;
        });
    }

    @Input() 
    get knownEmail() {
        return this._knownEmail;
    }

    @Input() 
    knownEmailOptin : boolean;

    set knownEmail(value) {
        this._knownEmail = value;
        setTimeout(() => {
            if (!this.email)
                this.email = value;
        })
    }

    firstName : string;
    email : string;
    password1 : string;
    password2 : string;
    newsletter : boolean;
    zip : string;

    ngOnInit() {
        this.subsink.add(this.userService.userChanged.subscribe(user => this.user = user));
    }

    ngOnDestroy() {
        this.subsink.unsubscribe();
    }

    submitting = false;
    extendedEmailNote = false;

    async submit() {

        if (!this.password1 && !this.password2) {
            this.errorMessage = 'Please choose a password for your account';
            return;
        }

        if (this.password1 !== this.password2) {
            this.errorMessage = 'Passwords do not match';
            return;
        }

        if (!this.firstName && !this.knownFirstName) {
            this.errorMessage = 'Please enter your first name so we know what to call you';
            return;
        }

        // CRITICAL SECTION: NO ASYNC //
        if (this.submitting)
          return;
        this.submitting = true;
        this.extendedEmailNote = false;
        this.errorMessage = null;
        this.emailErrorMessage = null;

        // CRITICAL SECTION: NO ASYNC //
        
        this.phase = 'loading';
        
        // Make sure the UI has updated before we proceed
        await sleep(0);

        let emailError = this.userService.validateEmail(this.email);
        if (emailError) {
            this.emailErrorMessage = emailError;
            this.submitting = false;
            this.phase = 'form';
            return;
        }
        
        ////////////////////////////////////////

        let userToken : UserAuthToken;

        try {
            userToken = <any> await this.userApi.signup({ 
                email: this.email,
                password: this.password1,
                first_name: this.firstName || this.knownFirstName,
                email_marketing_optin: this.knownEmailOptin || this.newsletter,
                source: this.userService.recentSource || 'free-signup/embedded',
                // Not added in the backend yet, but may be useful in the future
                //source_stamp: (this.userService.sourceStamp || new Date()).toISOString(),
                source_first_time: this.userService.sourceFirstTime,
                zip: this.zip,
                //recaptcha_token: this.recaptchaToken
            }).toPromise();
        } catch (e) {
            if (e.json)
                e = e.json();
            
            console.error("Received error while signing up:");
            console.dir(e);
    
            this.errorMessage = e.message || e.error;
            setTimeout(() => {
                this.submitting = false;
                this.phase = 'form';
            }, 3000);
    
            return;
        }
    
        if (!userToken.token) {
            this.shell.showDialog(MessageDialogComponent, "Confirm your email address", "You must confirm your email address before logging in. Please check your email to proceed.")
            this.submitting = false;
            this.phase = 'form';
            return;
        }
    
        Logger.inspect("UserToken", userToken);
    
        this.userService.onboarding = true;
        //this.userService.bounceURL = null;
        
        // Save authentication first (before completeLogin) so that sendRegisteredBeacons 
        // knows what user just signed up
        
        this.userService.stayAfterLogin();
        this.userService.saveAuthentication(userToken.user, userToken.token);
        await this.userService.sendRegisteredBeacons();
        this.userService.completeLogin(userToken.user, userToken.token);

        this.phase = 'success';
    }

    showLogin() {
        this.shell.showDialog(LoginComponent);
    }

    get termsUrl() {
        return `${environment.urls.accounts}/terms`;
    }

    get privacyPolicyUrl() {
        return `${environment.urls.accounts}/privacy`;
    }

    get coreValuesUrl() {
        return `${environment.urls.accounts}/core-values`;
    }

    oauthUrlFor(provider : string) {
      let queryString = this.buildQuery({
        app: 'web',
        source: this.userService.recentSource || 'free-signup',
        source_first_time: this.userService.sourceFirstTime ? 'true' : 'false',
        return_url: this.location.path()
      });
  
      return `${environment.urls.platform}/sign-in-with/${provider}?${queryString}`;
    }

    private buildQuery(params : Record<string,any>) {
        return Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&');
    }
}